import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import { AuthContext } from "../contexts";
import { headIdFor } from "./variantFamilies";

const emptyFavorites = {
  favoriteIds: new Set(),
  isFavorite: () => false,
  toggleFavorite: () => {},
};

export const FavoritesContext = createContext(emptyFavorites);

export const FavoritesProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());

  useEffect(() => {
    let active = true;
    if (!user?.id) {
      setFavoriteIds(new Set());
      return () => { active = false; };
    }
    setFavoriteIds(new Set());
    supabase.from("favorites").select("product_id").then(({ data, error }) => {
      if (!active) return;
      if (error) {
        console.error("Could not load favorites:", error);
        return;
      }
      setFavoriteIds(new Set((data || []).map(row => row.product_id)));
    });
    return () => { active = false; };
  }, [user?.id]);

  const toggleFavorite = useCallback(async productId => {
    if (!user?.id) return;
    const headId = headIdFor(productId);
    const removing = favoriteIds.has(headId);
    setFavoriteIds(current => {
      const next = new Set(current);
      if (removing) next.delete(headId);
      else next.add(headId);
      return next;
    });

    const request = removing
      ? supabase.from("favorites").delete().eq("product_id", headId)
      : supabase.from("favorites").insert({ product_id: headId });
    let error;
    try {
      ({ error } = await request);
    } catch (caught) {
      error = caught;
    }
    if (!error) return;
    console.error("Could not update favorite:", error);
    setFavoriteIds(current => {
      const next = new Set(current);
      if (removing) next.add(headId);
      else next.delete(headId);
      return next;
    });
  }, [favoriteIds, user?.id]);

  const value = useMemo(() => ({
    favoriteIds,
    isFavorite: productId => favoriteIds.has(headIdFor(productId)),
    toggleFavorite,
  }), [favoriteIds, toggleFavorite]);

  return createElement(FavoritesContext.Provider, { value }, children);
};

export const useFavorites = () => useContext(FavoritesContext) || emptyFavorites;
